import {
  BoxProps,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import { useOnboardingContext } from "@/common/contexts/onboarding";
import { ApiEnumName } from "@/common/services/enum";
import { OnboardingStatus } from "@/core/api/generated/v1";

import InlineApiEnumValue from "../../../Enum/InlineApiEnumValue";
import AppIcon from "../../../Icons/AppIcon";
import AppTypography from "../../../Text/AppTypography";
import AppTooltip from "../../../Tooltip/AppTooltip";

interface Props {
  sx?: BoxProps["sx"];
}

export function OnboardingStepChecklistCompact({ sx }: Props) {
  const onboardingContext = useOnboardingContext();

  return (
    <List sx={sx} dense disablePadding>
      {onboardingContext.enabledStepDefinitions?.map((stepDefinition, index) => {
        const stepPair = onboardingContext.getStepPairByDefinitionId(stepDefinition.id);
        const status = stepPair?.step?.status || OnboardingStatus.NotStarted;
        const isCompleted = status === OnboardingStatus.Completed;
        const isSkipped = status === OnboardingStatus.Skipped;

        return (
          <ListItem key={index} dense disablePadding disableGutters>
            <ListItemButton
              sx={{ p: (th) => th.spacing(1, 0) }}
              dense
              disableGutters
              onClick={() => {
                if (isCompleted || isSkipped) {
                  onboardingContext.startStep(stepDefinition.id || "");
                } else {
                  onboardingContext.completeStep(stepDefinition.id || "");
                }
              }}
            >
              <AppTooltip
                title={isCompleted || isSkipped ? "Mark as in progress" : "Mark as complete"}
                enterDelayPreset='large'
              >
                <ListItemIcon>
                  {isCompleted ? (
                    <AppIcon of='checkCircleOutline' color='primary' fontSize='medium' />
                  ) : isSkipped ? (
                    <AppIcon
                      of='cancelOutline'
                      fontSize='medium'
                      sx={{ color: (th) => th.palette.text.secondary }}
                    />
                  ) : (
                    <AppIcon of='circleOutline' color='text' fontSize='medium' />
                  )}
                </ListItemIcon>
              </AppTooltip>

              <ListItemText
                sx={{
                  m: 0,
                  ...((isCompleted || isSkipped) && {
                    color: "text.secondary",
                    textDecoration: "line-through",
                  }),
                }}
                primary={
                  <AppTooltip
                    title={
                      <InlineApiEnumValue type={ApiEnumName.OnboardingStatus} value={status} />
                    }
                    enterDelayPreset='large'
                  >
                    <AppTypography sx={{ width: "fit-content" }} variant='body1'>
                      {stepDefinition.title}
                    </AppTypography>
                  </AppTooltip>
                }
                disableTypography
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}
